import { useState } from 'react';
import { toast } from 'sonner';
import { useConnectLemlistMutation } from '@/store/features/crm.feature';
import {
  ApiKeyConnectDialog,
  KeyHelpLink,
} from './api-key-connect-dialog.component';

/**
 * Connect Lemlist with its API key (`/crm/lemlist/connect`): its card on
 * Integrations and Clu's connection card open the same dialog. `onConnected`
 * runs once the key is accepted, after the dialog closes.
 */
export function LemlistConnectDialog({
  open,
  onOpenChange,
  onConnected,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConnected?: () => void;
}) {
  const [apiKey, setApiKey] = useState('');
  const [connectLemlist, { isLoading }] = useConnectLemlistMutation();

  const handleConnect = async () => {
    if (!apiKey.trim()) {
      toast.error('Please enter your Lemlist API key');
      return;
    }
    try {
      await connectLemlist({ api_key: apiKey.trim() }).unwrap();
      onOpenChange(false);
      setApiKey('');
      onConnected?.();
    } catch (err) {
      const message =
        (err as { data?: { detail?: string } })?.data?.detail ||
        'Failed to connect Lemlist. Please check your API key.';
      toast.error(message);
    }
  };

  return (
    <ApiKeyConnectDialog
      name='Lemlist'
      open={open}
      onOpenChange={(next) => {
        onOpenChange(next);
        if (!next) setApiKey('');
      }}
      help={
        <>
          Enter your Lemlist API key to connect. You can find it in{' '}
          <KeyHelpLink href='https://app.lemlist.com/settings/integrations'>
            Lemlist Settings &rarr; Integrations
          </KeyHelpLink>
          .
        </>
      }
      value={apiKey}
      onValueChange={setApiKey}
      onSubmit={() => void handleConnect()}
      busy={isLoading}
    />
  );
}
